import type { API } from 'chessboard/api/config/types';
import type { Board } from 'chessboard/model/board/types';
import { Core } from 'chessboard/core/config/types';
import { createAPI } from 'chessboard/api';
import { createCore } from 'chessboard/core';
import { createCoreEvent } from 'chessboard/core/model/create-core-event';
import type { CreateGameOptions, GameAPI } from 'chessboard/types';
import { createQueue } from 'utils/queue';
import { createStandardPlugins } from 'chessboard/plugins';
import type { UserEventHandlers } from 'chessboard/create-user-event-handlers';
import { createUserEventHandlers } from 'chessboard/create-user-event-handlers';
import { getDefaultOptions } from 'chessboard/model/board/get-default-options';
import { createChessboardPluginsAPI } from 'chessboard/core/plugins';
import {
  shouldSetRenderer,
  checkWebGLSupport,
  defaultOptions,
} from 'chessboard/core/model/should-set-renderer';
import { getRenderer } from 'chessboard/core/model/get-renderer';
import type { Modes } from 'modes/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import { Pointerdown, Pointermove, Pointerup } from 'chessboard/model/dom/get-pointer-events';
import type { Renderer } from 'chessboard/renderers/config/types';
import { getMode as getModeChunk } from 'modes/model/get-mode';
import { createIsDragging } from 'api/is-dragging';

// added on build/install
import { CHESSBOARD_VERSION } from 'src/version';

/**
 * Create a function that creates an integrated chess game. Pass a renderer
 * middleware - and an optional function that determines board position
 * from a pointer event - and receive back a function that can be used to
 * initialize a game with that renderer.
 *
 * @example
 * const api = createGame({
 *   el, init, mode, options, renderer,
 * });
 *
 * // api.move
 * // api.selectLineEnd
 * // api.destroy
 * // etc...
 */
export function createGame({
  api: existingAPI,
  apiOptions,
  el,
  emitter,
  init,
  logger,
  mode,
  options = getDefaultOptions(),
  plugins = createStandardPlugins(),
  renderer: customRenderer,
  monitorUntrustedEvents = false,
}: CreateGameOptions): GameAPI {
  /**
   * The API handles the chess logic and maintains the move history.
   */
  const api: API.ExtendedAPI =
    existingAPI || createAPI({ eventEmitter: emitter, logger, options: apiOptions || {} });

  /**
   * The board options.
   */
  const boardOptions = { ...getDefaultOptions(), ...options };

  /**
   * Set board id
   */
  el.id = `board-${boardOptions.id}`;

  /**
   * The plugins API handles registration of board plugins.
   */
  const pluginsAPI = createChessboardPluginsAPI();

  /**
   * Function that changes the renderer.
   */
  const setRenderer = createSetRenderer();

  /**
   * Get the renderer, using the passed option or inferring based
   * on piece style and animation type.
   */
  let renderer: Renderer.Model | undefined;

  /**
   * User event listeners.
   */
  let listeners: UserEventHandlers;

  /**
   * Create the core.
   */
  const core = createCore({
    params: { api, el, options: boardOptions }, // context parameters
    plugins: pluginsAPI,
  });

  pluginsAPI.setCreatePluginContext(core.getContext());

  /**
   * Ensure API is ready before emitting the created event
   */
  const gameAPI: GameAPI = Object.assign(api, {
    destroy,
    getMode: core.getMode,
    getOptions,
    getVersion: () => CHESSBOARD_VERSION,
    isDragging: createIsDragging(core.getContext()),
    getRenderer: () => renderer,
    playSound,
    plugins: pluginsAPI,
    resize,
    // Low-level access to the board middleware chain for testing purposes
    run: runCoreEvent,
    setMode,
    setOptions,
    setRenderer,
  });

  const setModeResult = setMode(mode);

  if ('name' in setModeResult) {
    finishInit();
  } else {
    setModeResult.then(finishInit);
  }

  return gameAPI;

  /**
   * Finalize initialization
   */
  function finishInit() {
    pluginsAPI.add(plugins);

    /**
     * This fires the first event through the middleware (an options event), allowing
     * middlewares to do setup work.
     */
    runCoreEvent(createCoreEvent(Core.InstanceEvents.Create));

    /**
     * Attach listeners for the user events.
     */
    el.addEventListener('contextmenu', (e) => {
      e.stopPropagation();
      e.preventDefault();
      return false;
    });

    /**
     * Attach the listener for API events. When an API method is called, it emits an event
     * with the relevant information. Here we're going to listen to it and pass it through
     * the middleware chain.
     */
    api.onAll(runCoreEvent);

    api.emit(
      Core.InstanceEvents.Create,
      createCoreEvent(Core.InstanceEvents.Create, core.getMode().name),
    );

    setRenderer(customRenderer).then(() => {
      /**
       * Now that we're listening to the API events, let's initialize the API with data from
       * our createGame function parameters.
       */
      if (init) {
        api.load(init);
      }
    });
  }

  /**
   * Destroy the game, removing all event listeners. This doesn't affect the DOM elements.
   */
  function destroy(): void {
    destroyRenderer();

    api.offAll(runCoreEvent);

    const destroyEvent = createCoreEvent(Core.InstanceEvents.Destroy);

    /**
     * Fire destroy event
     */
    runCoreEvent(destroyEvent);

    /**
     * Alert all listeners.
     */
    api.emit(Core.InstanceEvents.Destroy, destroyEvent);
  }

  /**
   * Destroy the renderer, removing all event listeners.
   */
  function destroyRenderer(): void {
    core.setRenderer();

    renderer?.offAll(api.logger.log);
    renderer?.destroy();

    renderer = undefined;

    /**
     * Remove event listeners.
     */
    el.removeEventListener(Pointerdown, listeners?.handlePointerdown);
    document.removeEventListener(Pointerdown, listeners?.handleDocumentPointerdown);
    document.removeEventListener(Pointermove, listeners?.handlePointermove);
    document.removeEventListener(Pointerup, listeners?.handlePointerup);
  }

  /**
   * Get a shallow copy of the options object.
   */
  function getOptions() {
    return { ...boardOptions };
  }

  /**
   * Run a core event. This an abstraction that allows us to overwrite "run" in
   * this scope and allow the event listeners to call the new run function.
   */
  function runCoreEvent(event: Core.Event): Core.Event {
    core.run(event);

    return event;
  }

  /**
   * Create and fire a sound event.
   */
  function playSound(sound: string): void {
    runCoreEvent(createCoreEvent(Core.SoundEvents.Play, { sound }));
  }

  /**
   * Resize the board. If no dimensions are given it should try to fill its container.
   */
  function resize(width?: number, height?: number): void {
    runCoreEvent(createCoreEvent(Core.UIEvents.Resize, { height, width }));
  }

  /**
   * Set the current playing mode
   */
  function setMode(
    m: Modes.CreateMode.Builder | Modes.CreateMode.SetMode,
  ): Modes.API.Extended | Promise<Modes.API.Extended> {
    if ('modeType' in m) {
      return getModeChunk(m.modeType).then((factory) => {
        const builder = factory.create(m.options);

        return setModeWithBuilder(builder);
      });
    }

    return setModeWithBuilder(m);
  }

  /**
   * Set the current playing mode via builder
   */
  function setModeWithBuilder(m: Modes.CreateMode.Builder): Modes.API.Extended {
    const oldMode: Modes.API.Extended = core && core.getMode();

    // Destroy the old mode
    if (oldMode) {
      oldMode.plugins?.forEach((plugin: Plugins.Model) => pluginsAPI.remove(plugin.name));

      pluginsAPI.get().forEach((plugin) => plugin.destroyModeOverrides?.());

      oldMode.destroy?.();
    }

    // Create the new mode
    const newMode = m(api);

    // Determine whether or not the new mode should use the playingAs value
    api.usePlayingAs(newMode.usePlayingAs);

    if (newMode.plugins) {
      pluginsAPI.add(newMode.plugins);
    }

    pluginsAPI
      .get()
      .forEach((plugin) => plugin.modeOverrides && newMode.extendMode(plugin.modeOverrides));

    if (core) {
      core.setMode(newMode);

      runCoreEvent(createCoreEvent(Core.InstanceEvents.ModeChanged));

      api.emit(
        Core.InstanceEvents.ModeChanged,
        createCoreEvent(Core.InstanceEvents.ModeChanged, core.getMode().name),
      );
    }

    return newMode;
  }

  /**
   * Set the options, firing an event through the middleware chain,
   * so that middleware can update.
   */
  function setOptions(o?: Partial<Board.Options>): void {
    const oldOptions = { ...boardOptions };
    Object.assign(boardOptions || {}, o);

    if (o && renderer && shouldSetRenderer(o, oldOptions, boardOptions, renderer.name)) {
      setRenderer();
    }

    // Emit as API even so that it can be received both by the `run` function and externally.
    api.emit(Core.OptionsEvents.Update, createCoreEvent(Core.OptionsEvents.Update, o));
  }

  /**
   * Set the renderer, passing a custom one or determining based on board options.
   */
  function createSetRenderer() {
    const queue = createQueue();

    /**
     * Add the set renderer call to the queue.
     */
    return async function set(newRenderer?: Renderer.Model): Promise<void> {
      queue.add(handler, newRenderer);
    };

    /**
     * The logic handling the actual renderer change.
     */
    async function handler(newRenderer?: Renderer.Model): Promise<void> {
      destroyRenderer();

      if (newRenderer) {
        renderer = newRenderer;
      } else {
        try {
          if (!checkWebGLSupport(boardOptions)) {
            // ! Performs in-place mutations on board options if unsupported.
            api.emit(
              Core.InstanceEvents.RendererSetFailed,
              createCoreEvent(Core.InstanceEvents.RendererSetFailed, {
                message: 'WebGL is not supported by the browser. Will fallback to default.',
                isWebGLRenderError: true,
              }),
            );
          }
          renderer = (await getRenderer(boardOptions)).createRenderer({
            el,
            options: boardOptions,
          });
        } catch (e) {
          api.emit(
            Core.InstanceEvents.RendererSetFailed,
            createCoreEvent(Core.InstanceEvents.RendererSetFailed, {
              message: 'Problem creating renderer. Will fallback to default.',
              error: e,
            }),
          );
          defaultOptions(boardOptions, true);
          renderer = (await getRenderer(boardOptions)).createRenderer({
            el,
            options: boardOptions,
          });
        }
      }

      core.setRenderer(renderer);

      /**
       * Add api methods that depend on the renderer.
       */
      Object.assign(gameAPI, {
        animationComplete: renderer.animationComplete,
        getPointerPosition: renderer.getPointerPosition,
        isAnimating: renderer.isAnimating,
      });

      /**
       * Creating the handlers for "user events": clicking, key presses, mouse wheel.
       * The renderer defines a function that converts XY coordinates from a pointer event
       * to board coordinates/square data. The reason why we need this is that we have a
       * 3D board that has a different calculation.
       */
      listeners = createUserEventHandlers({
        el,
        getPointerPosition: renderer.getPointerPosition,
        options: boardOptions,
        run: runCoreEvent,
        monitorUntrustedEvents,
        api,
      });

      el.addEventListener(Pointerdown, listeners.handlePointerdown);
      // pointermove and pointer up are attached only after pointerdown
      document.addEventListener(Pointerdown, listeners.handleDocumentPointerdown);

      // log all events emitted by the renderer.
      renderer.onAll(api.logger.log);

      // forward initialization event from renderer to rest of listeners
      renderer.on(Core.InstanceEvents.WebGLAssetsInitialized, () => {
        api.emit(Core.InstanceEvents.WebGLAssetsInitialized, {
          type: Core.InstanceEvents.WebGLAssetsInitialized,
        });
      });

      api.emit(
        Core.InstanceEvents.RendererSet,
        createCoreEvent(Core.InstanceEvents.RendererSet, renderer.name),
      );
    }
  }
}
